import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { TranslationService } from 'src/app/services';
import { PlannerDayStateService } from '../../../../../services/planner-day-state.service';
import { RouteTimelineService } from '../../../services/route-timeline.service';
import { RegisterUserService } from 'src/app/pages/register-user/services';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import { EditRouteService } from './edit-route.service';
import { CustomRoute, Point } from 'src/app/pages/custom-route/interfaces/custom-route.interface';
import { SameDayGap } from '../interfaces/sortable-item.interface';
import { RouteDays } from 'src/app/pages/custom-route/shared/map/components/route-days/interfaces/route-days.interface';
import { normalizeValue } from 'src/app/pages/register-user/utils/value-normalizer';
import { CARD_ID_REGEX, SLOT_ID_REGEX } from '../config/constants';
import { END_TIME_HOUR, CARD_BASE_HEIGHT_PX } from '../../../config/constants';
import { computeMovedCardTimes } from 'src/app/pages/custom-route/helpers/compute-moved-card-times';
import { translateDayLabel } from 'src/app/pipes/day-label.pipe';

@Injectable({ providedIn: 'root' })
export class DailyRouteListService {

    private _translationService = inject(TranslationService);
    private _selectedLanguage = this._translationService.selectedLanguage;
    private _plannerDayStateService = inject(PlannerDayStateService);
    private _routeTimelineService = inject(RouteTimelineService);
    private _registerUserService = inject(RegisterUserService);
    private _customRouteService = inject(CustomRouteService);
    private _editRouteService = inject(EditRouteService);

    private _editedCustomRouteData = this._editRouteService.editedCustomRouteData;
    // Dynamic grid start per route (HRS-2640); replaces the fixed START_TIME_HOUR.
    private _routeStartHour = this._editRouteService.routeStartHour;
    public isDragging = this._routeTimelineService.isDragging;

    private _dayToShowIndex = this._plannerDayStateService.dayToShowIndex;
    private _numberOfDays = this._registerUserService.numberOfDaysCustomRoute;
    private _bodySyncEditedCustomRouteData = this._editRouteService.bodySyncEditedCustomRouteData;

    private _dailyCustomRouteData = computed(() =>
        this._editedCustomRouteData()?.dailyRoutes?.[this._dayToShowIndex()]
    );

    public routeDays = signal<RouteDays[]>([]);
    public previousTitles: string[] = [];

    private _dragCancelledByEsc = false;
    private _dragSourceDay = -1;
    private _dragSourceIndex = -1;
    private _draggedDurationSlots = 0;
    private _draggedDurationMs = 0;
    private _originalGapVisualIndex = -1;

    private _hoveredSlot = signal<{ dayIndex: number; insertIndex: number; subIndex: number } | null>(null);

    // Same-day sortable preview state (DOM-based, no flat list)
    private _sameDayGap = signal<SameDayGap | null>(null);
    private _previewDayIndex = signal<number | null>(null);

    private _setRouteDaysEffect = effect(() => {
        const customRouteData = this._editedCustomRouteData();
        if (!customRouteData || !this._numberOfDays()) return;

        const days = Array.from({ length: this._numberOfDays() }, (_, i) => (i + 1).toString());

        this.routeDays.set(days.map((day) => {
            const dayNumber = Number(day);
            const dayIndex = dayNumber - 1;

            const label = translateDayLabel(this._translationService, dayNumber, () => {
              const numberOfDayTranslated = this._translationService.instant(day);
              const dayWordTranslated = this._translationService.instant('día');
              const dayWordCapitalized = dayWordTranslated.charAt(0).toUpperCase() + dayWordTranslated.slice(1);
              return `${dayWordCapitalized} ${numberOfDayTranslated}`;
            });

            return {
                id: dayNumber,
                label,
                value: normalizeValue(label),
                iconName: this._getWeatherIconForDay(dayIndex)
            };
        }));
    }, { allowSignalWrites: true });

    private _setPreviousTitlesEffect = effect(() => {
        const dailyData = this._dailyCustomRouteData();
        if (!dailyData?.points) return;

        this.previousTitles = dailyData.points.map((_p, i) =>
            i === 0 ? dailyData.startPoint.address : dailyData.points[i - 1].title?.[this._selectedLanguage()]
        );
    }, { allowSignalWrites: true });

    // -- Same-day sortable preview API ------------------------------------------

    public isSameDayPreviewActive(dayIndex: number): boolean {
        return this._previewDayIndex() === dayIndex && this._sameDayGap() !== null;
    }

    public get previewGapOrder(): number | null {
        return this._sameDayGap()?.visualIndex ?? null;
    }

    public get draggedDurationSlots(): number {
        return this._draggedDurationSlots;
    }

    // -- Slot computation -------------------------------------------------------

    public getEmptyDaySlots(): number[] {
        return Array.from({ length: END_TIME_HOUR - this._routeStartHour() }, (_, i) => i);
    }

    public getLeadingSlots(dayIndex: number): number[] {
        const points = this._editedCustomRouteData()?.dailyRoutes[dayIndex]?.points;
        if (!points?.length) return [];

        const firstPointStart = new Date(points[0].startDate);

        // Align every day column with the timeline axis, which starts at the
        // route's start hour (UTC).
        const timelineStart = new Date(firstPointStart);
        timelineStart.setUTCHours(this._routeStartHour(), 0, 0, 0);

        const leadingMs = firstPointStart.getTime() - timelineStart.getTime();
        if (leadingMs <= 0) return [];

        const leadingHours = Math.floor(leadingMs / 3_600_000);
        return leadingHours > 0 ? Array.from({ length: leadingHours }, (_, i) => i) : [];
    }

    public getGapSlots(dayIndex: number, pointIndex: number): number[] {
        const points = this._editedCustomRouteData()?.dailyRoutes[dayIndex]?.points;
        if (!points || pointIndex <= 0) return [];

        const prevEnd = new Date(points[pointIndex - 1].endDate);
        const currentStart = new Date(points[pointIndex].startDate);
        if (!prevEnd || !currentStart) return [];

        const gapHours = Math.floor(
            (currentStart.getTime() - prevEnd.getTime()) / 3_600_000
        );

        return gapHours > 0 ? Array.from({ length: gapHours }, (_, i) => i) : [];
    }

    public getTrailingSlots(dayIndex: number): number[] {
        const points = this._editedCustomRouteData()?.dailyRoutes[dayIndex]?.points;
        if (!points || points.length === 0) return [];

        const lastEnd = new Date(points[points.length - 1].endDate);
        if (isNaN(lastEnd.getTime())) return [];

        const endHours = lastEnd.getUTCHours();
        if (endHours === 0) return [];

        const minutesUntilMidnight = (END_TIME_HOUR - endHours) * 60 - lastEnd.getUTCMinutes();
        const trailingHours = Math.floor(minutesUntilMidnight / 60);

        return trailingHours > 0 ? Array.from({ length: trailingHours }, (_, i) => i) : [];
    }

    // -- Drag & drop ------------------------------------------------------------

    public isSlotDroppable(slotDayIndex: number, slotInsertIndex: number): boolean {
        if (!this._routeTimelineService.isDragging()) return false;

        const isSameDay = slotDayIndex === this._dragSourceDay;
        const isAdjacent = isSameDay
            && (slotInsertIndex === this._dragSourceIndex || slotInsertIndex === this._dragSourceIndex + 1);

        if (!isAdjacent) return true;

        const points = this._editedCustomRouteData()?.dailyRoutes[slotDayIndex]?.points;
        if (!points) return false;

        const isTrailingSlot = slotInsertIndex === points.length;
        const isLastCard = this._dragSourceIndex === points.length - 1;
        if (isLastCard && isTrailingSlot) return true;

        const isLeadingSlotBeforeFirstCard = slotInsertIndex === 0 && this._dragSourceIndex === 0;
        if (isLeadingSlotBeforeFirstCard && this.getLeadingSlots(slotDayIndex).length > 0) return true;

        const isGapSlotBeforeCard = slotInsertIndex === this._dragSourceIndex && this._dragSourceIndex > 0;
        if (isGapSlotBeforeCard && this.getGapSlots(slotDayIndex, this._dragSourceIndex).length > 0) return true;

        return false;
    }

    public isSwapTarget(dayIndex: number, cardIndex: number): boolean {
        if (!this._routeTimelineService.isDragging()) return false;
        if (dayIndex === this._dragSourceDay) return false;
        return this._isSwapDurationCompatible(dayIndex, cardIndex);
    }

    public isSwapIncompatible(dayIndex: number, cardIndex: number): boolean {
        if (!this._routeTimelineService.isDragging()) return false;
        if (dayIndex === this._dragSourceDay) return false;
        return !this._isSwapDurationCompatible(dayIndex, cardIndex);
    }

    private _isSwapDurationCompatible(dayIndex: number, cardIndex: number): boolean {
        const targetAvailableMs = this._getAvailableSpaceMs(dayIndex, cardIndex);
        const sourceAvailableMs = this._getAvailableSpaceMs(this._dragSourceDay, this._dragSourceIndex);
        const targetDurationMs = this._getPointDurationMs(dayIndex, cardIndex);

        if (targetDurationMs === 0 || this._draggedDurationMs === 0) return false;

        return this._draggedDurationMs <= targetAvailableMs && targetDurationMs <= sourceAvailableMs;
    }

    private _getPointDurationMs(dayIndex: number, cardIndex: number): number {
        const points = this._editedCustomRouteData()?.dailyRoutes[dayIndex]?.points;
        if (!points?.[cardIndex]) return 0;
        const p = points[cardIndex];
        return new Date(p.endDate).getTime() - new Date(p.startDate).getTime();
    }

    private _getAvailableSpaceMs(dayIndex: number, cardIndex: number): number {
        const points = this._editedCustomRouteData()?.dailyRoutes[dayIndex]?.points;
        if (!points?.[cardIndex]) return 0;

        const pointStart = new Date(points[cardIndex].startDate).getTime();

        if (cardIndex < points.length - 1) {
            return new Date(points[cardIndex + 1].startDate).getTime() - pointStart;
        }

        const dayEnd = new Date(points[cardIndex].startDate);
        dayEnd.setUTCHours(END_TIME_HOUR, 0, 0, 0);
        return dayEnd.getTime() - pointStart;
    }

    public isSlotInDropRange(dayIndex: number, insertIndex: number, subIndex: number): boolean {
        const hovered = this._hoveredSlot();
        if (!hovered) return false;
        if (hovered.dayIndex !== dayIndex || hovered.insertIndex !== insertIndex) return false;

        const groupSize = this._getSlotGroupSize(dayIndex, insertIndex);

        if (this._isSamePositionSlot(dayIndex, insertIndex)) {
            const slotsToHighlight = Math.min(this._draggedDurationSlots, groupSize - hovered.subIndex);
            return subIndex >= hovered.subIndex && subIndex < hovered.subIndex + slotsToHighlight;
        }

        if (groupSize < this._draggedDurationSlots) return false;

        const effectiveStart = this._clampSubIndex(groupSize, hovered.subIndex);
        return subIndex >= effectiveStart && subIndex < effectiveStart + this._draggedDurationSlots;
    }

    public isSlotInsufficient(dayIndex: number, insertIndex: number, subIndex: number): boolean {
        if (this._isSamePositionSlot(dayIndex, insertIndex)) return false;

        const hovered = this._hoveredSlot();
        if (!hovered) return false;
        if (hovered.dayIndex !== dayIndex || hovered.insertIndex !== insertIndex) return false;

        const groupSize = this._getSlotGroupSize(dayIndex, insertIndex);
        if (groupSize >= this._draggedDurationSlots) return false;

        return subIndex >= 0 && subIndex < groupSize;
    }

    private _isSamePositionSlot(slotDayIndex: number, slotInsertIndex: number): boolean {
        if (slotDayIndex !== this._dragSourceDay) return false;

        let targetPointIndex = slotInsertIndex;
        if (this._dragSourceIndex < slotInsertIndex) targetPointIndex--;

        return this._dragSourceIndex === targetPointIndex;
    }

    private _getSlotGroupSize(dayIndex: number, insertIndex: number): number {
        const points = this._editedCustomRouteData()?.dailyRoutes[dayIndex]?.points;
        if (!points?.length) return this.getEmptyDaySlots().length;

        if (insertIndex === 0) return this.getLeadingSlots(dayIndex).length;
        if (insertIndex === points.length) return this.getTrailingSlots(dayIndex).length;
        return this.getGapSlots(dayIndex, insertIndex).length;
    }

    private _clampSubIndex(groupSize: number, rawSubIndex: number): number {
        const maxStart = groupSize - this._draggedDurationSlots;
        return Math.max(0, Math.min(rawSubIndex, maxStart));
    }

    public updateHoverPosition(x: number, y: number): void {
        // Check if cursor is within the source day column
        if (this._isCursorInSourceDayColumn(x, y)) {
            this._updateSameDayPreview(y);
            return;
        }

        // Cursor is outside source day column -- clear same-day preview
        this._clearSameDayPreview();

        // Existing cross-day slot-based hover logic
        const elements = document.elementsFromPoint(x, y);

        for (const el of elements) {
            if ((el as HTMLElement).classList.contains('cdk-drag-preview')) continue;

            const target = (el as HTMLElement).closest('[id^="slot-"]') as HTMLElement;
            if (!target) continue;

            const match = target.id.match(SLOT_ID_REGEX);
            if (match && match[3] !== undefined) {
                this._hoveredSlot.set({
                    dayIndex: +match[1],
                    insertIndex: +match[2],
                    subIndex: +match[3],
                });
                return;
            }
        }

        this._hoveredSlot.set(null);
    }

    private _isCursorInSourceDayColumn(x: number, _y: number): boolean {
        const columnElements = document.querySelectorAll('.daily-route-list__list-item');
        const sourceColumn = columnElements[this._dragSourceDay] as HTMLElement | undefined;
        if (!sourceColumn) return false;

        const rect = sourceColumn.getBoundingClientRect();
        // Only check horizontal bounds. Vertical position determines gap index,
        // not whether we're in the same-day column (enables drag-up past top edge).
        return x >= rect.left && x <= rect.right;
    }

    /**
     * DOM-based same-day preview: walks visible items, parses IDs to identify
     * cards vs slots, determines gap position and target point index.
     */
    private _updateSameDayPreview(cursorY: number): void {
        const sourceColumn = this._getSourceDayColumn();
        if (!sourceColumn) return;

        const allItems = sourceColumn.querySelectorAll(':scope > .point-list__item, :scope > .point-list__add-item');
        if (!allItems.length) return;

        // Collect visible items with identity parsed from DOM IDs
        type VisibleItem = {
            el: HTMLElement;
            midY: number;
            kind: 'card' | 'slot';
            cardIndex?: number;       // original point index from DOM id
            slotInsertIndex?: number;  // from DOM id
            slotSubIndex?: number;     // from DOM id
        };
        const visibleItems: VisibleItem[] = [];

        for (let i = 0; i < allItems.length; i++) {
            const el = allItems[i] as HTMLElement;

            // Skip CDK placeholder
            if (el.classList.contains('cdk-drag-placeholder')) continue;
            // Skip dragged element (CDK hides with display:none)
            if (el.style.display === 'none') continue;

            const rect = el.getBoundingClientRect();
            if (rect.height === 0) continue;

            const cardMatch = el.id.match(CARD_ID_REGEX);
            if (cardMatch) {
                visibleItems.push({
                    el, midY: rect.top + rect.height / 2,
                    kind: 'card', cardIndex: +cardMatch[2]
                });
                continue;
            }

            const slotMatch = el.id.match(SLOT_ID_REGEX);
            if (slotMatch) {
                visibleItems.push({
                    el, midY: rect.top + rect.height / 2,
                    kind: 'slot',
                    slotInsertIndex: +slotMatch[2],
                    slotSubIndex: slotMatch[3] !== undefined ? +slotMatch[3] : 0
                });
            }
        }

        if (!visibleItems.length) return;

        // Determine gap visual index from cursor Y
        let gapVisualIndex = visibleItems.length; // default: after everything
        for (let i = 0; i < visibleItems.length; i++) {
            if (cursorY < visibleItems[i].midY) {
                gapVisualIndex = i;
                break;
            }
        }

        // Count non-dragged card elements before the gap → targetPointIndex
        // Card indices from DOM are from the ORIGINAL points array (includes dragged card).
        // After removing the dragged card via splice, the count of remaining cards
        // before the gap IS the target index in the post-removal array.
        let targetPointIndex = 0;
        for (let i = 0; i < gapVisualIndex; i++) {
            if (visibleItems[i].kind === 'card') targetPointIndex++;
        }

        // Detect if gap is in a slot region (card order unchanged but time position changed)
        let isSlotGap = false;
        let slotInsertIndex: number | undefined;
        let slotSubIndex: number | undefined;

        if (targetPointIndex === this._dragSourceIndex) {
            // Card order hasn't changed. Determine direction by comparing gap
            // position with the card's original position (stable, not cursor-based).
            const isDraggedUp = gapVisualIndex < this._originalGapVisualIndex;
            const isDraggedDown = gapVisualIndex > this._originalGapVisualIndex;

            if (isDraggedUp && gapVisualIndex < visibleItems.length && visibleItems[gapVisualIndex].kind === 'slot') {
                // Drag-up: use the slot at the gap position (the one card moved past going up)
                isSlotGap = true;
                slotInsertIndex = visibleItems[gapVisualIndex].slotInsertIndex;
                slotSubIndex = visibleItems[gapVisualIndex].slotSubIndex ?? 0;
            } else if (isDraggedDown && gapVisualIndex > 0 && visibleItems[gapVisualIndex - 1].kind === 'slot') {
                // Drag-down: use the slot before the gap (the one card moved past going down)
                isSlotGap = true;
                slotInsertIndex = visibleItems[gapVisualIndex - 1].slotInsertIndex;
                slotSubIndex = visibleItems[gapVisualIndex - 1].slotSubIndex ?? 0;
            }
        }

        // Count consecutive slot items immediately before the gap position.
        // This tells us how many hour-slots past the predecessor the ghost is.
        let slotsBeforeGap = 0;
        for (let i = gapVisualIndex - 1; i >= 0; i--) {
            if (visibleItems[i].kind === 'slot') slotsBeforeGap++;
            else break;
        }

        // When the gap falls past all visible items (cursor below the last
        // trailing slot), the last slot IS the drop target -- not a slot the
        // card goes past. Without this, the card lands 1 hour too late.
        if (gapVisualIndex === visibleItems.length && slotsBeforeGap > 0) {
            slotsBeforeGap--;
        }

        // Compute the target hour from cursor Y on the fixed hour grid.
        // The CSS order manipulation displaces slots, making slotsBeforeGap
        // unreliable when the ghost gap shifts slot midY values. The hour grid
        // (column top = the route's start hour) is fixed and immune to CSS reorder.
        const columnRect = sourceColumn.getBoundingClientRect();
        const cursorHour = Math.floor((cursorY - columnRect.top) / CARD_BASE_HEIGHT_PX) + this._routeStartHour();

        // Store original gap position on first frame (for direction detection)
        if (this._originalGapVisualIndex === -1) {
            this._originalGapVisualIndex = gapVisualIndex;
        }

        // Store gap state
        this._sameDayGap.set({
            visualIndex: gapVisualIndex,
            targetPointIndex,
            isSlotGap,
            slotInsertIndex,
            slotSubIndex,
            slotsBeforeGap,
            cursorHour,
        });
        this._previewDayIndex.set(this._dragSourceDay);

        // Apply CSS order to DOM elements
        this._applyPreviewDomChanges(sourceColumn, visibleItems.map(v => v.el), gapVisualIndex);
    }

    private _getSourceDayColumn(): HTMLElement | null {
        const columnElements = document.querySelectorAll('.daily-route-list__list-item');
        const sourceColumn = columnElements[this._dragSourceDay] as HTMLElement | undefined;
        if (!sourceColumn) return null;
        return sourceColumn.querySelector('.daily-route-list__point-list');
    }

    private _applyPreviewDomChanges(
        listEl: HTMLElement,
        visibleElements: HTMLElement[],
        gapIndex: number
    ): void {
        // Set order on each visible item (slots + cards)
        for (let i = 0; i < visibleElements.length; i++) {
            const order = i >= gapIndex ? i + 1 : i;
            visibleElements[i].style.order = String(order);
        }

        // Hide the CDK placeholder entirely so it doesn't create extra flex gaps
        const placeholder = listEl.querySelector('.cdk-drag-placeholder') as HTMLElement | null;
        if (placeholder) {
            placeholder.style.display = 'none';
        }
    }

    private _clearSameDayPreview(): void {
        if (this._sameDayGap() === null) return;

        // Reset DOM order styles
        const sourceColumn = this._getSourceDayColumn();
        if (sourceColumn) {
            const allItems = sourceColumn.querySelectorAll(':scope > .point-list__item, :scope > .point-list__add-item');
            for (let i = 0; i < allItems.length; i++) {
                (allItems[i] as HTMLElement).style.order = '';
            }
            // Restore CDK placeholder
            const placeholder = sourceColumn.querySelector('.cdk-drag-placeholder') as HTMLElement | null;
            if (placeholder) {
                placeholder.style.display = '';
            }
        }

        this._sameDayGap.set(null);
        this._previewDayIndex.set(null);
        this._originalGapVisualIndex = -1;
    }

    public clearHoverState(): void {
        this._hoveredSlot.set(null);
        this._draggedDurationSlots = 0;
        this._draggedDurationMs = 0;
        this._clearSameDayPreview();
    }

    public onDragStarted(dayIndex: number, pointIndex: number, point: Point): void {
        this._dragSourceDay = dayIndex;
        this._dragSourceIndex = pointIndex;
        this._draggedDurationMs = new Date(point.endDate).getTime() - new Date(point.startDate).getTime();
        this._draggedDurationSlots = Math.max(1, Math.ceil(this._draggedDurationMs / 3_600_000));
        this._routeTimelineService.setIsDragging(true);
        // Same-day preview is initialized lazily in _updateSameDayPreview
    }

    public onEscapeKey(): void {
        if (!this._routeTimelineService.isDragging()) return;
        this._dragCancelledByEsc = true;
        document.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true, view: window }));
    }

    public onDragEnded(draggedPoint: Point, dropPoint: { x: number; y: number } | undefined): void {
        this._routeTimelineService.setIsDragging(false);
        this._hoveredSlot.set(null);

        if (this._dragCancelledByEsc) {
            this._dragCancelledByEsc = false;
            this._draggedDurationSlots = 0;
            this._clearSameDayPreview();
            return;
        }

        if (!draggedPoint || !dropPoint) {
            this._draggedDurationSlots = 0;
            this._clearSameDayPreview();
            return;
        }

        // Same-day preview is active: commit from preview state
        if (this._sameDayGap() !== null) {
            this._commitSameDayDrop(draggedPoint);
            this._draggedDurationSlots = 0;
            return;
        }

        // Cross-day: existing behavior
        this._resolveDropTarget(draggedPoint, dropPoint.x, dropPoint.y);
        this._draggedDurationSlots = 0;
    }

    private _commitSameDayDrop(draggedPoint: Point): void {
        const gap = this._sameDayGap();
        const dayIndex = this._dragSourceDay;
        const sourcePointIndex = this._dragSourceIndex;

        this._clearSameDayPreview();

        if (!gap) return;

        const { targetPointIndex, isSlotGap, slotInsertIndex, slotSubIndex, slotsBeforeGap, cursorHour } = gap;

        // No-op: card order unchanged and not in a slot gap
        if (targetPointIndex === sourcePointIndex && !isSlotGap) return;

        if (targetPointIndex !== sourcePointIndex) {
            // Card order changed: reorder + resolve times preserving gaps
            this._editRouteService.movePointInEditedRoute(
                dayIndex, sourcePointIndex,
                dayIndex, targetPointIndex
            );
            this._resolveTimesAfterReorder(dayIndex, sourcePointIndex, targetPointIndex, slotsBeforeGap, cursorHour);
        } else {
            // Card order unchanged but moved past slots: update start time
            // Compute the slot's actual hourTime directly
            const targetStartDate = this._getSlotHourTime(dayIndex, slotInsertIndex!, slotSubIndex!);
            if (targetStartDate) {
                this._editRouteService.movePointInEditedRoute(
                    dayIndex, sourcePointIndex,
                    dayIndex, targetPointIndex,
                    targetStartDate
                );
            }
        }

        this._editRouteService.syncEditedCustomRouteDataWithPythonBackendServiceSignal(this._bodySyncEditedCustomRouteData());
    }

    /**
     * Computes the actual start time of a slot from its DOM ID coordinates.
     * Leading slots: timelineStart + subIndex * 1h
     * Gap/trailing slots: prevPoint.endDate + subIndex * 1h
     */
    private _getSlotHourTime(dayIndex: number, insertIndex: number, subIndex: number): Date | undefined {
        const points = this._editedCustomRouteData()?.dailyRoutes[dayIndex]?.points;
        if (!points?.length) return undefined;

        if (insertIndex === 0) {
            const firstStart = new Date(points[0].startDate);
            const timelineStart = new Date(firstStart);
            timelineStart.setUTCHours(this._routeStartHour(), 0, 0, 0);
            return new Date(timelineStart.getTime() + subIndex * 3_600_000);
        }

        const prevPoint = points[insertIndex - 1];
        if (!prevPoint) return undefined;

        // When the previous point is the dragged card itself, compute from its startDate
        // so each slot = 1 hour shift (e.g., 2h card at 17-19: subIndex=0 → 18:00, not 19:00)
        const isPrevPointSource = dayIndex === this._dragSourceDay && insertIndex - 1 === this._dragSourceIndex;
        if (isPrevPointSource) {
            const sourceStartMs = new Date(prevPoint.startDate).getTime();
            return new Date(sourceStartMs + (subIndex + 1) * 3_600_000);
        }

        const prevEndMs = new Date(prevPoint.endDate).getTime();
        return new Date(prevEndMs + subIndex * 3_600_000);
    }

    /**
     * After a same-day card reorder:
     * 1. Shifts displaced cards earlier to close the gap (sortable push-up)
     * 2. Sets the moved card's time based on the drop position
     * All dates stored as ISO strings to match the rest of the codebase.
     */
    private _resolveTimesAfterReorder(dayIndex: number, sourceIndex: number, targetIndex: number, slotsBeforeGap: number, cursorHour?: number): void {
        const route = this._editRouteService.editedCustomRouteData();
        if (!route) return;

        const day = route.dailyRoutes[dayIndex];
        if (!day?.points.length) return;

        const MS_PER_HOUR = 3_600_000;
        const points = [...day.points];
        const movedPoint = points[targetIndex];
        const rawDurationMs = new Date(movedPoint.endDate).getTime() - new Date(movedPoint.startDate).getTime();

        // --- 1. Shift displaced cards to close the gap left by the removed card ---
        if (sourceIndex < targetIndex) {
            const movedStartMs = new Date(movedPoint.startDate).getTime();
            const movedEndMs = new Date(movedPoint.endDate).getTime();
            const slotShiftMs = Math.ceil(movedEndMs / MS_PER_HOUR) * MS_PER_HOUR - movedStartMs;

            for (let i = sourceIndex; i < targetIndex; i++) {
                const currStartMs = new Date(points[i].startDate).getTime();
                const currDurationMs = new Date(points[i].endDate).getTime() - currStartMs;
                const visualDurationMs = Math.ceil(currDurationMs / MS_PER_HOUR) * MS_PER_HOUR;
                const newStartMs = Math.ceil((currStartMs - slotShiftMs) / MS_PER_HOUR) * MS_PER_HOUR;
                points[i] = {
                    ...points[i],
                    startDate: new Date(newStartMs).toISOString() as unknown as Date,
                    endDate: new Date(newStartMs + visualDurationMs).toISOString() as unknown as Date,
                };
            }
        }

        // --- 2. Compute moved card time ---
        // cursorHour: the absolute hour on the fixed grid where the user's cursor is.
        //   Immune to CSS order displacement that makes slotsBeforeGap unreliable.
        // Tight pack (slotsBeforeGap=0): use SHIFTED predecessor (card goes right
        //   after displaced cards in their new positions).
        // Slot gap (slotsBeforeGap>0): use cursorHour to compute absolute start time.
        const shiftedPredecessorEndMs = targetIndex > 0
            ? new Date(points[targetIndex - 1].endDate).getTime()
            : null;

        let startMs: number;
        let endMs: number;

        if (slotsBeforeGap === 0 || cursorHour === undefined) {
            // Tight pack or no cursor hour: use shifted predecessor + computeMovedCardTimes
            const originalRoute = this._customRouteService.originalCustomRouteData();
            const originalFirstStart = originalRoute?.dailyRoutes[dayIndex]?.points[0]?.startDate;
            // HRS-2658: sin snapshot original (p.ej. carga por token tras un
            // planner/exit), el ancla de primera posición es la tarjeta
            // desplazada (la que era primera antes del reorden) — nunca la
            // propia tarjeta movida (points[0] ya reordenado), que la dejaba
            // "clavada" en su hora anterior.
            const displacedFirstStart = targetIndex === 0 ? points[1]?.startDate : undefined;
            const firstPositionAnchor = originalFirstStart ?? displacedFirstStart;

            const result = computeMovedCardTimes({
                predecessorEndMs: shiftedPredecessorEndMs,
                slotsBeforeGap: 0,
                rawDurationMs,
                dayStartMs: new Date(points[0].startDate).getTime(),
                isFirstPosition: targetIndex === 0,
                originalFirstStartMs: firstPositionAnchor ? new Date(firstPositionAnchor).getTime() : undefined,
            });
            startMs = result.startMs;
            endMs = result.endMs;
        } else {
            // Slot gap: use cursor hour as absolute target time
            const dayDate = new Date(points[0].startDate);
            dayDate.setUTCHours(cursorHour, 0, 0, 0);
            const visualDurationMs = Math.ceil(rawDurationMs / MS_PER_HOUR) * MS_PER_HOUR;

            startMs = dayDate.getTime();

            // Clamp to day boundary
            const dayEnd = new Date(points[0].startDate);
            dayEnd.setUTCHours(END_TIME_HOUR, 0, 0, 0);
            const maxStartMs = Math.floor((dayEnd.getTime() - visualDurationMs) / MS_PER_HOUR) * MS_PER_HOUR;
            if (startMs > maxStartMs) {
                startMs = maxStartMs;
            }

            endMs = startMs + visualDurationMs;
        }

        points[targetIndex] = {
            ...movedPoint,
            startDate: new Date(startMs).toISOString() as unknown as Date,
            endDate: new Date(endMs).toISOString() as unknown as Date,
        };

        // --- 3. Forward pass: push cards after insertion point to avoid overlaps ---
        // When a card is inserted between two others, cards after it may overlap.
        for (let i = targetIndex + 1; i < points.length; i++) {
            const prevEndMs = new Date(points[i - 1].endDate).getTime();
            const minStartMs = Math.ceil(prevEndMs / MS_PER_HOUR) * MS_PER_HOUR;
            const currStartMs = new Date(points[i].startDate).getTime();

            if (currStartMs < minStartMs) {
                const currDurationMs = new Date(points[i].endDate).getTime() - currStartMs;
                const visualDurationMs = Math.ceil(currDurationMs / MS_PER_HOUR) * MS_PER_HOUR;
                points[i] = {
                    ...points[i],
                    startDate: new Date(minStartMs).toISOString() as unknown as Date,
                    endDate: new Date(minStartMs + visualDurationMs).toISOString() as unknown as Date,
                };
            }
        }

        // --- 4. Normalize remaining dates to ISO strings ---
        // Some points may have Date objects (from API/adapter), others ISO strings.
        // Mixed formats cause the render to interpret times at different timezones.
        for (let i = 0; i < points.length; i++) {
            if (i === targetIndex) continue; // already ISO
            if (sourceIndex < targetIndex && i >= sourceIndex && i < targetIndex) continue; // already ISO from step 2
            // Skip cards already normalized by forward pass
            const alreadyISO = new Date(points[i].startDate).toISOString() === String(points[i].startDate);
            if (alreadyISO) continue;
            points[i] = {
                ...points[i],
                startDate: new Date(points[i].startDate).toISOString() as unknown as Date,
                endDate: new Date(points[i].endDate).toISOString() as unknown as Date,
            };
        }

        const updatedRoute: CustomRoute = {
            ...route,
            dailyRoutes: route.dailyRoutes.map((d, i) =>
                i === dayIndex ? { ...d, points } : d
            )
        };

        this._editRouteService.setEditedCustomRouteData(updatedRoute);
    }

    // -- Private drag helpers ---------------------------------------------------

    private _resolveDropTarget(draggedPoint: Point, x: number, y: number): void {
        const elements = document.elementsFromPoint(x, y);

        for (const el of elements) {
            if ((el as HTMLElement).classList.contains('cdk-drag-preview')) continue;

            const target = (el as HTMLElement).closest('[id^="slot-"], [id^="card-"]') as HTMLElement;
            if (!target) continue;

            const slotMatch = target.id.match(SLOT_ID_REGEX);
            if (slotMatch) {
                const slotSubIndex = slotMatch[3] !== undefined ? +slotMatch[3] : undefined;
                this._handleDropOnSlot(draggedPoint, +slotMatch[1], +slotMatch[2], slotSubIndex);
                return;
            }

            const cardMatch = target.id.match(CARD_ID_REGEX);
            if (cardMatch) {
                this._handleSwapOnCard(draggedPoint, +cardMatch[1], +cardMatch[2]);
                return;
            }
        }
    }

    private _findSourceIndices(draggedPoint: Point): { dayIndex: number; pointIndex: number } | null {
        const route = this._editedCustomRouteData();
        if (!route) return null;

        for (let d = 0; d < route.dailyRoutes.length; d++) {
            const idx = route.dailyRoutes[d].points.findIndex(p => p.id === draggedPoint.id);
            if (idx !== -1) return { dayIndex: d, pointIndex: idx };
        }
        return null;
    }

    private _handleDropOnSlot(draggedPoint: Point, targetDayIndex: number, insertIndex: number, slotSubIndex?: number): void {
        const source = this._findSourceIndices(draggedPoint);
        if (!source) return;

        let targetPointIndex = insertIndex;
        if (source.dayIndex === targetDayIndex && source.pointIndex < insertIndex) {
            targetPointIndex--;
        }

        const isSamePosition = source.dayIndex === targetDayIndex && source.pointIndex === targetPointIndex;

        let effectiveSubIndex = slotSubIndex;
        if (slotSubIndex !== undefined && !isSamePosition) {
            const groupSize = this._getSlotGroupSize(targetDayIndex, insertIndex);
            if (groupSize < this._draggedDurationSlots) return;
            effectiveSubIndex = this._clampSubIndex(groupSize, slotSubIndex);
        }

        const targetStartDate = this._calcSlotStartDate(targetDayIndex, insertIndex, effectiveSubIndex);
        if (isSamePosition && !targetStartDate) return;

        this._executeDragOperation({
            mutate: () => this._editRouteService.movePointInEditedRoute(
                source.dayIndex, source.pointIndex,
                targetDayIndex, targetPointIndex,
                targetStartDate
            ),
        });
    }

    private _handleSwapOnCard(draggedPoint: Point, targetDayIndex: number, targetPointIndex: number): void {
        const source = this._findSourceIndices(draggedPoint);
        if (!source) return;
        if (source.dayIndex === targetDayIndex && source.pointIndex === targetPointIndex) return;
        if (!this._isSwapDurationCompatible(targetDayIndex, targetPointIndex)) return;

        this._executeDragOperation({
            mutate: () => this._editRouteService.swapPointsInEditedRoute(
                source.dayIndex, source.pointIndex,
                targetDayIndex, targetPointIndex
            ),
        });
    }

    private _executeDragOperation(config: {
        mutate: () => void;
    }): void {
        config.mutate();
        this._editRouteService.syncEditedCustomRouteDataWithPythonBackendServiceSignal(this._bodySyncEditedCustomRouteData());
    }

    private _calcSlotStartDate(dayIndex: number, insertIndex: number, slotSubIndex?: number): Date | undefined {
        if (slotSubIndex === undefined) return undefined;

        if (insertIndex === 0) {
            const day = this._editedCustomRouteData()?.dailyRoutes[dayIndex];

            // Empty day: no points to anchor on, derive the timeline start from
            // the day's own date so the dropped card lands on the targeted hour.
            if (!day?.points?.length) {
                if (!day?.date) return undefined;
                const timelineStart = new Date(day.date);
                timelineStart.setUTCHours(this._routeStartHour(), 0, 0, 0);
                return new Date(timelineStart.getTime() + slotSubIndex * 3_600_000);
            }

            // Leading slots start from timelineStart (the route's start hour, UTC)
            const firstStart = new Date(day.points[0].startDate);
            const timelineStart = new Date(firstStart);
            timelineStart.setUTCHours(this._routeStartHour(), 0, 0, 0);
            return new Date(timelineStart.getTime() + slotSubIndex * 3_600_000);
        }

        const points = this._editedCustomRouteData()?.dailyRoutes[dayIndex]?.points;
        const prevPoint = points?.[insertIndex - 1];
        if (!prevPoint) return undefined;

        const isPrevPointSource = dayIndex === this._dragSourceDay && insertIndex - 1 === this._dragSourceIndex;
        if (isPrevPointSource) {
            const startMs = new Date(prevPoint.startDate).getTime();
            return new Date(startMs + (slotSubIndex + 1) * 3_600_000);
        }

        const prevEndMs = new Date(prevPoint.endDate).getTime();
        return new Date(prevEndMs + slotSubIndex * 3_600_000);
    }

    // -- Private helpers --------------------------------------------------------

    private _getWeatherIconForDay(dayIndex: number): string {
        const customRouteData = this._editedCustomRouteData();
        if (!customRouteData || !customRouteData.dailyRoutes[dayIndex]) {
            return 'default-weather-icon';
        }
        return customRouteData.dailyRoutes[dayIndex].weatherIconName || 'default-weather-icon';
    }
}
